import { useCallback, useEffect, useRef, useState } from 'react';
import { createDecoder, decodeImageFile, type Decoder } from './decoder';
import { beepScan } from './feedback';

/**
 * QR2's camera scanner (apps/scan/Camera.svelte) ported to React: back camera, native detector
 * first, a scan every ~110 ms (170 ms with the wasm decoder), the same code ignored for 3 s,
 * torch and zoom when the camera offers them, the screen kept awake while scanning, the camera
 * stopped when the page is hidden, manual entry and a photo as fallbacks.
 */
type Status = 'starting' | 'running' | 'nocamera' | 'denied' | 'stopped';

export function Camera({ active, hint, onDetect, compact = false }: { active: boolean; hint?: string; onDetect: (code: string, source: 'scan' | 'manual') => void; compact?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>('starting');
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [zoomLevels, setZoomLevels] = useState<number[]>([]);
  const [zoom, setZoomState] = useState(1);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [fileMsg, setFileMsg] = useState<string | null>(null);
  const [decoderKind, setDecoderKind] = useState<Decoder['kind'] | null>(null);

  const stream = useRef<MediaStream | null>(null);
  const track = useRef<MediaStreamTrack | null>(null);
  const decoder = useRef<Decoder | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const decoding = useRef(false);
  const last = useRef({ code: '', at: 0 });
  const wakeLock = useRef<{ release?: () => void } | null>(null);
  const destroyed = useRef(false);
  const activeRef = useRef(active);
  const onDetectRef = useRef(onDetect);
  activeRef.current = active;
  onDetectRef.current = onDetect;

  const releaseWakeLock = () => {
    try { wakeLock.current?.release?.(); } catch { /* ignore */ }
    wakeLock.current = null;
  };

  const stop = useCallback((final: boolean) => {
    window.clearTimeout(timer.current);
    if (stream.current) {
      stream.current.getTracks().forEach((t) => t.stop());
      stream.current = null;
      track.current = null;
    }
    if (video.current) video.current.srcObject = null;
    setTorchOn(false);
    releaseWakeLock();
    if (final) setStatus('stopped');
  }, []);

  const handle = useCallback((code: string) => {
    const now = Date.now();
    if (code === last.current.code && now - last.current.at < 3000) return;
    last.current = { code, at: now };
    beepScan();
    onDetectRef.current(code, 'scan');
  }, []);

  const loop = useCallback(() => {
    window.clearTimeout(timer.current);
    const d = decoder.current;
    if (!d || !video.current) return;
    timer.current = window.setTimeout(async () => {
      if (activeRef.current && !decoding.current && video.current && video.current.readyState >= 2) {
        decoding.current = true;
        try {
          const code = await d.detect(video.current);
          if (code) handle(code);
        } catch {
          // a single frame failing is not interesting
        }
        decoding.current = false;
      }
      if (!destroyed.current && stream.current) loop();
    }, d.kind === 'native' ? 110 : 170);
  }, [handle]);

  const start = useCallback(async () => {
    if (destroyed.current) return;
    stop(false);
    setStatus('starting');
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('nocamera');
      return;
    }
    try {
      decoder.current ??= await createDecoder();
      setDecoderKind(decoder.current.kind);
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 1280 } } });
      if (destroyed.current || !video.current) {
        stop(false);
        return;
      }
      video.current.srcObject = stream.current;
      await video.current.play();
      track.current = stream.current.getVideoTracks()[0] ?? null;
      const caps = (track.current?.getCapabilities?.() ?? {}) as { torch?: boolean; zoom?: { max?: number } };
      setTorchSupported(!!caps.torch);
      if (caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > 1) {
        const max = Math.min(caps.zoom.max, 5);
        setZoomLevels([1, 2, max].filter((v, i, a) => v <= max && a.indexOf(v) === i));
        setZoomState(Math.max(1, Math.min(max, ((track.current?.getSettings?.() as { zoom?: number }) ?? {}).zoom ?? 1)));
      } else {
        setZoomLevels([]);
      }
      setStatus('running');
      try { wakeLock.current = await (navigator as unknown as { wakeLock?: { request(t: string): Promise<{ release(): void }> } }).wakeLock?.request('screen') ?? null; } catch { wakeLock.current = null; }
      loop();
    } catch (e) {
      stop(false);
      const name = (e as { name?: string }).name;
      setStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'nocamera');
    }
  }, [loop, stop]);

  useEffect(() => {
    destroyed.current = false;
    void start();
    const onVisibility = () => (document.hidden ? stop(true) : void start());
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      destroyed.current = true;
      document.removeEventListener('visibilitychange', onVisibility);
      stop(true);
    };
  }, [start, stop]);

  const toggleTorch = async () => {
    if (!track.current) return;
    try {
      await track.current.applyConstraints({ advanced: [{ torch: !torchOn } as MediaTrackConstraintSet] });
      setTorchOn(!torchOn);
    } catch {
      setTorchSupported(false);
    }
  };
  const setZoom = async (level: number) => {
    if (!track.current) return;
    try {
      await track.current.applyConstraints({ advanced: [{ zoom: level } as MediaTrackConstraintSet] });
      setZoomState(level);
    } catch { /* ignore */ }
  };
  const submitManual = (e: React.FormEvent) => {
    e.preventDefault();
    const v = manualValue.trim();
    if (!v) return;
    setManualOpen(false);
    setManualValue('');
    onDetectRef.current(v, 'manual');
  };
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = '';
    if (!file) return;
    setFileMsg('Leder efter en kode…');
    try {
      decoder.current ??= await createDecoder();
      const code = await decodeImageFile(decoder.current, file);
      if (code) {
        setFileMsg(null);
        beepScan();
        onDetectRef.current(code, 'manual');
      } else setFileMsg('Ingen kode fundet på billedet');
    } catch {
      setFileMsg('Ingen kode fundet på billedet');
    }
  };

  const pill = 'pointer-events-auto rounded-full bg-white/15 px-3.5 py-2 text-[13px] font-bold text-white backdrop-blur min-h-10';
  return (
    <div className={`relative overflow-hidden bg-[#0b0f18] ${compact ? 'h-56' : 'flex-1 min-h-[45vh]'}`}>
      <video ref={video} playsInline muted autoPlay className={`absolute inset-0 h-full w-full object-cover ${active ? '' : 'brightness-[.35] saturate-[.6]'}`} />
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-[#e8ecf5]">
        {status === 'running' && (
          <>
            <div className="relative aspect-[3/2] w-[min(78vw,320px)]">
              <span className="absolute left-0 top-0 h-8 w-8 rounded-tl-[10px] border-l-4 border-t-4 border-white" />
              <span className="absolute right-0 top-0 h-8 w-8 rounded-tr-[10px] border-r-4 border-t-4 border-white" />
              <span className="absolute bottom-0 left-0 h-8 w-8 rounded-bl-[10px] border-b-4 border-l-4 border-white" />
              <span className="absolute bottom-0 right-0 h-8 w-8 rounded-br-[10px] border-b-4 border-r-4 border-white" />
              {active && <span className="absolute left-3 right-3 top-1/2 h-0.5 bg-cyan shadow-[0_0_14px_#52cdff]" />}
            </div>
            {hint && <p className="mt-3 max-w-[85%] text-center text-sm font-semibold [text-shadow:0_1px_2px_rgba(0,0,0,.7)]">{active ? hint : 'Pause'}</p>}
          </>
        )}
        {status === 'starting' && <p className="text-sm font-semibold">Starter kamera…</p>}
        {(status === 'denied' || status === 'nocamera' || status === 'stopped') && (
          <div className="grid justify-items-center gap-2 px-6 text-center">
            <p className="text-sm font-semibold">{status === 'denied' ? 'Kameraet er ikke tilladt. Giv adgang i browserens indstillinger.' : status === 'nocamera' ? 'Intet kamera fundet. Indtast koden i stedet.' : 'Kameraet er sat på pause.'}</p>
            <button type="button" className={pill} onClick={() => void start()}>Prøv igen</button>
          </div>
        )}
        <div className="absolute bottom-3 left-0 right-0 flex flex-wrap justify-center gap-2 px-3">
          {torchSupported && <button type="button" className={`${pill} ${torchOn ? '!bg-white !text-[#0b0f18]' : ''}`} onClick={toggleTorch}>🔦 Lys</button>}
          {zoomLevels.length > 1 && (
            <span className="pointer-events-auto inline-flex rounded-full bg-white/15 backdrop-blur">
              {zoomLevels.map((z) => <button key={z} type="button" className={`rounded-full px-3 py-2 text-[13px] font-bold ${zoom === z ? 'bg-white text-[#0b0f18]' : 'text-white'}`} onClick={() => setZoom(z)}>{z}×</button>)}
            </span>
          )}
          <button type="button" className={pill} onClick={() => setManualOpen(!manualOpen)}>⌨ Indtast</button>
          <button type="button" className={pill} onClick={() => fileInput.current?.click()}>🖼 Foto</button>
          <input ref={fileInput} type="file" accept="image/*" capture="environment" className="sr-only" onChange={onFile} tabIndex={-1} />
        </div>
        {fileMsg && <p className="absolute bottom-16 rounded-lg bg-black/55 px-3 py-1.5 text-[13px] font-bold">{fileMsg}</p>}
        {decoderKind === 'wasm' && <span className="absolute bottom-1 right-2 text-[10px] opacity-50">wasm</span>}
      </div>
      {manualOpen && (
        <form className="absolute inset-x-0 bottom-0 grid gap-1.5 rounded-t-2xl bg-white p-3 text-ink shadow-[0_-8px_24px_rgba(0,0,0,.3)]" onSubmit={submitManual}>
          <label className="text-xs font-medium text-muted">Indtast kode</label>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input className="input" autoFocus value={manualValue} onChange={(e) => setManualValue(e.target.value)} autoComplete="off" autoCapitalize="none" placeholder="Lokation, SKU eller EAN" />
            <button className="btn btn-primary" disabled={!manualValue.trim()}>OK</button>
          </div>
        </form>
      )}
    </div>
  );
}
